import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { Country } from "@omni/types/enums/country.enum";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useCallback, useState } from "react";

import { ValidationAddress } from "@/components/Address/AddressValidation/context/addressValidation.context";
import BPostService from "@/services/bpost.service";

type OnValidCallback = (addressLine: string) => void;

type AddressFormBlockContextValue = {
    isValidating: boolean;
    addPrefix: (fieldName: string, separator?: string) => string;
    addressStatus: AddressValidationStatus;
    disabled?: boolean;
    displayAddressFields: boolean;
    setDisplayAddressFields: (display: boolean) => void;
    validate: (
        values: ValidationAddress,
        cbOnValid: OnValidCallback
    ) => Promise<void>;
};

export type AddressFormBlockContextProviderProps = {
    addressStatus: AddressValidationStatus;
    addressType: AddressType;
    disabled?: boolean;
    skipValidation?: boolean;
    country?: string;
};

export const AddressFormBlockContext = createContext<
    AddressFormBlockContextValue | undefined
>(undefined);

AddressFormBlockContext.displayName = "AddressFormBlockContext";

export const shouldDisplayAddressFields = (
    addressStatus: AddressValidationStatus,
    country?: string | null
) =>
    country !== Country.Be ||
    addressStatus === AddressValidationStatus.NoValidationNeeded ||
    addressStatus === AddressValidationStatus.Invalid ||
    addressStatus === AddressValidationStatus.OverruledByCustomer;

export const AddressFormBlockContextProvider = ({
    addressStatus,
    addressType,
    disabled,
    skipValidation,
    country = Country.Be,
    children
}: RequireChildrenFor<AddressFormBlockContextProviderProps>) => {
    const [displayAddressFields, setDisplayAddressFields] = useState(
        shouldDisplayAddressFields(addressStatus, country)
    );
    const [isValidating, setIsValidating] = useState(false);

    const addPrefix = useCallback(
        (fieldName: string) => {
            const prefix =
                addressType === AddressType.Default ? "" : addressType;
            return `${prefix.toLowerCase()}-${fieldName}`;
        },
        [addressType]
    );

    const validate = useCallback(
        async (address: ValidationAddress, cbOnValid: OnValidCallback) => {
            if (skipValidation) return;
            if (country !== Country.Be) return;

            const validationStatus = address.validationStatus;

            if (
                validationStatus === AddressValidationStatus.Validated ||
                validationStatus ===
                    AddressValidationStatus.NoValidationNeeded ||
                validationStatus === AddressValidationStatus.OverruledByCustomer
            ) {
                return;
            }

            setIsValidating(true);

            try {
                const validationResult = await BPostService.validateAddress({
                    city: address.city,
                    number: address.number,
                    street: address.street,
                    box: address.box,
                    zipCode: address.zipCode
                });

                const officialAddress = validationResult.isValid
                    ? validationResult.officialAddress
                    : undefined;

                setDisplayAddressFields(!officialAddress);

                if (officialAddress) {
                    cbOnValid(officialAddress.line);
                }
            } finally {
                setIsValidating(false);
            }
        },
        [country, skipValidation]
    );

    return (
        <AddressFormBlockContext.Provider
            value={{
                isValidating,
                addPrefix,
                addressStatus,
                disabled,
                displayAddressFields,
                setDisplayAddressFields,
                validate
            }}
        >
            {children}
        </AddressFormBlockContext.Provider>
    );
};

export const useAddressFormBlockContext = (): AddressFormBlockContextValue => {
    const context = use<AddressFormBlockContextValue | undefined>(
        AddressFormBlockContext
    );
    if (!isDefined(context)) {
        throw new Error(`${AddressFormBlockContext.displayName} is undefined.`);
    }
    return context;
};
